import React, { memo } from "react";
import { ProductCategory } from "@utils/constants";
import BulkActionsModal from "../BulkActionsModal";
import ShareModal from "@components/molecules/Share";
import CollectionModal from "@components/molecules/Collections/CollectionModal";
import CreateNewCollectionModal from "@components/molecules/Collections/CreateCollectionModal";
import RequestBMHModal from "../RequestBMHModal";
import ShareExcelModal from "@components/molecules/Share/components/ShareExcelModal";
import GalleryViewModal from "@components/molecules/GalleryViewModel/GalleryViewModal";
import LoaderModel from "@components/molecules/LoaderModel/LoaderModel";
import { ShareType } from "@components/molecules/Share/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ThreeDPropertiesProvider } from "../../../contexts/ThreeDPropertiesContext";
import { BulkTasks, ShareMenuOptionEnum } from "@utils/product/menuBuilders";
import { SearchResultsScreenModals } from "@utils/product/searchResultConstants";
import DownloadCSVModal, {
  FileFormat,
  ShareMode,
} from "@components/molecules/Share/components/DownloadCSVModal";
import OrderCartModal from "../OrderCartDialog";
import { useAppSettings } from "@redux/slices/globalSlice";

interface ShowModalState {
  name: string | null;
  selectedOptions: any;
  shareMenuOption: string | null;
  openBulkDialog: boolean;
  openOrderCartDialog: boolean;
  isBuyNow?: boolean;
  orderCartItem?: any;
}

interface ItemDetails {
  itemIdArray?: any[];
  mItem?: any;
}

interface SearchResultModalsProps {
  showModal: ShowModalState;
  showBMHModal: boolean;
  showCertificate: boolean;
  setShowModal: (state: ShowModalState) => void;
  setShowBMHModal: (show: boolean) => void;
  setShowCertificate: (show: boolean) => void;
  setRemoveFavoritesItem: (remove: boolean) => void;
  setSingleItem: (item: any) => void;
  setCertificateURL: (url: string) => void;
  setOpenBulkDialog: (open: boolean) => void;
  setOpenOrderCartDialog: (open: boolean) => void;
  selectedItems: any[];
  itemDetails: ItemDetails;
  singleItem: any;
  certificateURL: string;
  productType: ProductCategory;
  priceMode: string;
  removeFavoritesItem: boolean;
  isGuest: boolean;
  favoriteRemoved: () => void;
  bulkActionCallback: (action: any, options?: any) => void;
  sendEmailExcelData: (itemIds: number[] | null, formData: any) => void;
  DownloadCSVData: (
    itemIds: number[] | null,
    format: FileFormat,
    actionType: ShareMode,
    selectedItemsCount: number
  ) => void;
  addCollectionSuccess: (data: any) => void;
  bulkActions: any;
  t: any;
  isMyInventory?: boolean;
  totalItems: number;
}

const SearchResultModals = memo<SearchResultModalsProps>(
  ({
    showModal,
    showBMHModal,
    showCertificate,
    setShowModal,
    setShowBMHModal,
    setShowCertificate,
    setRemoveFavoritesItem,
    setSingleItem,
    setCertificateURL,
    setOpenBulkDialog,
    setOpenOrderCartDialog,
    selectedItems,
    itemDetails,
    singleItem,
    certificateURL,
    productType,
    priceMode,
    removeFavoritesItem,
    isGuest,
    favoriteRemoved,
    bulkActionCallback,
    sendEmailExcelData,
    DownloadCSVData,
    addCollectionSuccess,
    bulkActions,
    t,
    isMyInventory = false,
    totalItems,
  }) => {
    const { showBulkRequestFeature } = useUserInfo();
    const maxItems = useAppSettings()?.maximum_exports_number;
    const closeModal = () => {
      setShowModal({
        name: null,
        selectedOptions: null,
        shareMenuOption: null,
        openBulkDialog: false,
      });
    };

    const closeCertificate = () => {
      setSingleItem({});
      setCertificateURL("");
      setShowCertificate(!showCertificate);
    };

    return (
      <>
        {showModal.name === SearchResultsScreenModals.COLLECTIONS_LIST && (
          <CollectionModal
            isOpen={
              showModal.name === SearchResultsScreenModals.COLLECTIONS_LIST
            }
            onClose={() => {
              closeModal();
              setRemoveFavoritesItem(false);
            }}
            itemIdArray={itemDetails?.itemIdArray}
            favItemIdArray={itemDetails?.itemIdArray}
            item={itemDetails?.mItem}
            collectionItemIdArray={[]}
            favoriteRemoved={favoriteRemoved}
            fromSearchResultScreen={true}
            enableToastMessage={true}
            removeFavoriteItem={removeFavoritesItem}
          />
        )}

        {showModal.name === SearchResultsScreenModals.EMAIL_EXCEL_MODAL && (
          <ShareExcelModal
            isOpen={
              showModal.name === SearchResultsScreenModals.EMAIL_EXCEL_MODAL
            }
            onClose={closeModal}
            enableToastMessage={true}
            selectedItemsCount={selectedItems?.length}
            fromFavoriteScreen={false}
            onSendEmail={(formData, mode) => {
              const itemIds =
                mode === "selected"
                  ? selectedItems?.map((_Item) => _Item?.id ?? 0)
                  : null;
              sendEmailExcelData(itemIds, formData);
            }}
          />
        )}

        {showModal.name === SearchResultsScreenModals.DOWNLOAD_CSV_PDF && (
          <DownloadCSVModal
            isOpen={
              showModal.name === SearchResultsScreenModals.DOWNLOAD_CSV_PDF
            }
            onClose={closeModal}
            enableToastMessage={true}
            selectedItemsCount={selectedItems?.length}
            fromFavoriteScreen={false}
            onSendEmail={(fileFormat, mode) => {
              const itemIds =
                mode === ShareMode.EXPORT_SELECTED_ITEMS
                  ? selectedItems?.map((_Item) => _Item?.id ?? 0)
                  : null;
              DownloadCSVData(
                itemIds,
                fileFormat,
                mode,
                mode === ShareMode.EXPORT_SELECTED_ITEMS
                  ? selectedItems?.length
                  : totalItems > maxItems
                  ? maxItems
                  : totalItems
              );
            }}
          />
        )}

        {showBMHModal && (
          <RequestBMHModal
            isOpen={showBMHModal}
            onClose={() => setShowBMHModal(false)}
            productType={productType}
            priceMode={priceMode}
            selectOption={bulkActionCallback}
          />
        )}

        {showModal.name === SearchResultsScreenModals.SHARE && (
          <ShareModal
            isOpen={showModal.name === SearchResultsScreenModals.SHARE}
            onClose={closeModal}
            {...(selectedItems.length <= 1 || itemDetails?.mItem !== undefined
              ? {
                  item:
                    selectedItems.length === 1
                      ? selectedItems[0]
                      : itemDetails?.mItem,
                }
              : {})}
            shareType={
              selectedItems.length === 1 ||
              showModal.shareMenuOption === ShareMenuOptionEnum.SINGLE
                ? ShareType.singleShare
                : ShareType.multiShare
            }
            bulkItems={
              showModal.shareMenuOption === ShareMenuOptionEnum.MULTIPLE
                ? selectedItems
                : []
            }
            selectedOption={showModal.selectedOptions}
          />
        )}

        {showModal.name === SearchResultsScreenModals.CREATE_NEW_COLLECTION && (
          <CreateNewCollectionModal
            isOpen={
              showModal.name === SearchResultsScreenModals.CREATE_NEW_COLLECTION
            }
            onClose={closeModal}
            onSuccess={(data: any) => addCollectionSuccess(data)}
          />
        )}

        {showModal.openBulkDialog && (
          <BulkActionsModal
            isOpen={!!showModal.openBulkDialog}
            onClose={() => setOpenBulkDialog(false)}
            menuOptions={BulkTasks(
              t,
              bulkActions,
              showBulkRequestFeature(productType, priceMode),
              isMyInventory
            )}
            itemCount={selectedItems.length ?? 0}
            totalItems={totalItems ?? 0}
            selectOption={bulkActionCallback}
          />
        )}

        {showModal.openOrderCartDialog && (
          <OrderCartModal
            isOpen={!!showModal.openOrderCartDialog}
            onClose={() => setOpenOrderCartDialog(false)}
            mItem={showModal.orderCartItem ?? selectedItems?.[0]}
            isBuyNow={showModal.isBuyNow ?? false}
          />
        )}

        {showCertificate && (
          <ThreeDPropertiesProvider>
            <GalleryViewModal
              isOpen={showCertificate}
              ReceivedMedia={[
                {
                  order: 0,
                  link: certificateURL,
                  type: "pdf",
                },
              ]}
              activeIndex={0}
              setActiveIndex={0}
              onClose={closeCertificate}
              productType={productType}
              isGuest={isGuest}
              showThumbnails={false}
              showSharableIcons={false}
              fromSearchResult={true}
              hideNavigationArrow={true}
              {...(singleItem && { itemDetail: singleItem })}
            />
          </ThreeDPropertiesProvider>
        )}

        {showModal.name === SearchResultsScreenModals.LOADER_MODAL && (
          <LoaderModel
            isOpen={showModal.name === SearchResultsScreenModals.LOADER_MODAL}
            onClose={closeModal}
          />
        )}
      </>
    );
  }
);

SearchResultModals.displayName = "SearchResultModals";

export default SearchResultModals;
